/**
 * Preview-step AI enrichment, in memory: the bridge between the Bulk AI Enrich
 * review grid (suggestion keys, stored-ref shape) and the bulk-import preview
 * rows (ScrapedWatchEntry). Nothing here touches Firestore — the rows and the
 * in-memory variant parents are written later by the import itself.
 */
import type { ScrapedWatchEntry } from './BulkImport';
import { ENRICH_FIELD_SPECS } from '../../services/bulkEnrichFields';

// AI suggestion keys → ScrapedWatchEntry keys.
//
// The enrichReference callable returns ~67 suggestion keys (the full field
// surface is enumerated in services/bulkEnrichFields.ts, the registry the
// standalone Bulk AI Enhance tool uses). This map is deliberately the SUBSET
// that has a home on ScrapedWatchEntry — the scraped-entry shape is flatter
// than WatchReference, so keys with no entry field (booleans like
// isChronograph/hasLume, indexColor, handTypes,
// subdials, strapWidth, strapBuckleType, limitedEditionCount,
// occasion, series, nickname, the electronics.* detail fields and the
// pocket-watch block) stay dropped rather than inventing entry fields the
// import mapper wouldn't read. Keys outside it are never proposed in the
// preview grid (its acceptsKey filter), so nothing ticked there is lost.
//
// Every target below is consumed by mapEntryToReference, so a filled value
// actually reaches Firestore on import.
export const AI_TO_ENTRY_FIELD: Record<string, keyof ScrapedWatchEntry> = {
  // Identity / classification
  model: 'model',
  collection: 'collection',
  watchType: 'watchType',
  description: 'description',
  // Movement. `calibre` targets `movement` (not the untyped `entry.calibre`
  // compat key) because the Check Calibres pass matches the calibre DB on
  // entry.movement; the readers downstream are `entry.calibre || entry.movement`
  // either way.
  calibre: 'movement',
  movementType: 'movementType',
  jewels: 'jewels',
  powerReserve: 'powerReserve',
  elecModule: 'module',
  // Case
  caseMaterial: 'caseMaterial',
  caseFinish: 'caseFinish',
  caseCoating: 'caseCoating',
  caseShape: 'caseShape',
  caseBack: 'caseBack',
  caseSize: 'caseSize',
  caseHeight: 'caseHeight',
  lugWidth: 'lugWidth',
  lugToLug: 'lugToLug',
  waterRes: 'waterRes',
  crystal: 'crystal',
  crownType: 'crownType',
  bezelType: 'bezelType',
  // `bezelMaterial` was on the dropped list until the bezel subsection gave
  // it an entry field; it maps to `bezelMaterial` here and to `bezel` on the
  // reference, which is where the material has always lived.
  bezelMaterial: 'bezelMaterial',
  bezelColors: 'bezelColors',
  // Dial & hands
  dialColor: 'dialColor',
  dialStyle: 'dialStyle',
  dialFinish: 'dialFinish',
  dialIndexes: 'dialIndexes',
  dialNumerals: 'dialNumerals',
  dialMaterial: 'dialMaterial',
  hands: 'hands',
  handsColor: 'handsColor',
  // Date complication — same key name on both sides. dateWheelTextColor is
  // the multi-select one; coerceSuggestion below joins the AI's array into the
  // comma-separated form the entry (and the reference) store.
  dateDisplay: 'dateDisplay',
  dateWindowPosition: 'dateWindowPosition',
  dateWindowFrame: 'dateWindowFrame',
  dateWheelColor: 'dateWheelColor',
  dateWheelTextColor: 'dateWheelTextColor',
  // Lume — same key name on both sides, all four single-select. `hasLume`
  // stays off this map: it is a boolean and ScrapedWatchEntry has no field
  // for it (the import text-detects it instead).
  lumePresence: 'lumePresence',
  lumeMaterial: 'lumeMaterial',
  lumeDayColor: 'lumeDayColor',
  lumeGlowColor: 'lumeGlowColor',
  // Subdial functions — same key name on both sides. The `subdials` COUNT
  // stays off this map: it is a number and ScrapedWatchEntry has no field for
  // it, so an AI count has nowhere to land on this path (the ref editor's
  // suggestion panel is where that one gets accepted).
  subdialTypes: 'subdialTypes',
  // Chapter ring & dial furniture — same key name on both sides. Three
  // single-select, plus `dialScales`, which is multi and lands comma-joined.
  chapterRing: 'chapterRing',
  minuteTrack: 'minuteTrack',
  dialScales: 'dialScales',
  dialFeatures: 'dialFeatures',
  logoType: 'logoType',
  // Strap
  strapMaterial: 'strapMaterial',
  strapColor: 'strapColor',
  // Complications
  functions: 'functions',
  // Production years. releaseYear was on the dropped list, so the preview grid
  // filtered it out: a bulk import never captured the year that a standalone
  // enrich of the same ref then proposed. mapEntryToReference writes all three
  // to `production.*`.
  releaseYear: 'productionReleaseYear',
  startYear: 'productionStartYear',
  endYear: 'productionEndYear',
};

/**
 * Coerce a suggestion value into the string shape ScrapedWatchEntry stores.
 * Multi-value fields (functions, dialColor, handsColor) can come back as
 * arrays — those persist as comma-joined strings on the entry, so join rather
 * than drop. Returns null for anything unusable (objects, booleans, empty).
 */
export const coerceSuggestion = (value: unknown): string | null => {
  if (typeof value === 'string') {
    const t = value.trim();
    return t === '' ? null : t;
  }
  if (typeof value === 'number') return Number.isFinite(value) ? String(value) : null;
  if (Array.isArray(value)) {
    const parts = value
      .filter((v) => typeof v === 'string' || typeof v === 'number')
      .map((v) => String(v).trim())
      .filter(Boolean);
    return parts.length ? parts.join(', ') : null;
  }
  return null;
};

/** A preview row's pseudo-reference id in the enrich grid (index into the import's items). */
export const previewRowId = (index: number) => `preview:${index}`;
const PREVIEW_ROW_PREFIX = 'preview:';

/** Entry keys owned by the six variant-level fields — never copied parent → variant. */
const VARIANT_LEVEL_ENTRY_KEYS = new Set<string>([
  'dialColor',
  'dialFinish',
  'dialStyle',
  'caseMaterial',
  'strapMaterial',
  'movement',
  'calibre',
]);

/**
 * Does the preview grid accept this AI key? Every key the grid registry knows —
 * the SAME set standalone enrichment writes (James, 2026-10-29: bulk import
 * must not accept a narrower set). Keys with a ScrapedWatchEntry field route
 * through {@link AI_TO_ENTRY_FIELD}; every other registry key rides
 * `_aiFields` (see {@link suggestionValuesToEntryFields}).
 */
export function previewAcceptsAiKey(key: string): boolean {
  return key === 'calibreTierId' || key in AI_TO_ENTRY_FIELD || key in ENRICH_FIELD_SPECS;
}

/**
 * Accepted suggestion values → entry fields (the curator ticked them, so they overwrite).
 *
 * A key with an entry field (AI_TO_ENTRY_FIELD) goes onto that field, as text.
 * Every OTHER grid-registry key becomes the registry's own write — its
 * `buildPatch`, the exact dotted-path patch standalone Bulk AI Enrich writes,
 * types included (booleans, numbers, arrays, comma-joined strings) — collected
 * in `_aiFields` and applied by mapEntryToReference on save. One write path for
 * both tools, so a field added to the registry reaches imports with no further
 * wiring.
 */
export function suggestionValuesToEntryFields(values: Record<string, unknown>): Partial<ScrapedWatchEntry> {
  const out: Record<string, unknown> = {};
  const aiFields: Record<string, unknown> = {};
  for (const [aiKey, value] of Object.entries(values)) {
    if (aiKey === 'calibreTierId') {
      // Read at write time by mapEntryToReference, like the calibre-check tier.
      if (typeof value === 'string' && value.trim()) out._calibreTierId = value.trim();
      continue;
    }
    const entryKey = AI_TO_ENTRY_FIELD[aiKey];
    if (entryKey) {
      const coerced = coerceSuggestion(value);
      if (coerced === null) continue;
      out[entryKey] = coerced;
      // A suggested calibre is a NAME: keep it whole as the explicit calibre.
      if (aiKey === 'calibre') out.calibre = coerced;
      continue;
    }
    const patch = ENRICH_FIELD_SPECS[aiKey]?.buildPatch(value);
    if (patch) Object.assign(aiFields, patch);
  }
  if (Object.keys(aiFields).length > 0) out._aiFields = aiFields;
  return out as Partial<ScrapedWatchEntry>;
}

/** Entry + accepted fields, with `_aiFields` merged path by path rather than replaced. */
function withFields<E extends ScrapedWatchEntry>(entry: E, fields: Partial<ScrapedWatchEntry>): E {
  const merged = { ...entry, ...fields } as E;
  if (entry._aiFields || fields._aiFields) merged._aiFields = { ...(entry._aiFields ?? {}), ...(fields._aiFields ?? {}) };
  return merged;
}

/**
 * Apply an entry's `_aiFields` to the reference mapEntryToReference built, LAST:
 * the curator's ticked AI value wins over the importer's own text detection
 * (isChronograph, series, specialEdition…), which still stands wherever the AI
 * proposed nothing. Nested paths ("movement.isChronograph") are set without
 * disturbing their siblings.
 */
export function applyAiFieldPatch<T extends Record<string, unknown>>(ref: T, patch: Record<string, unknown> | undefined): T {
  if (!patch) return ref;
  const out: Record<string, unknown> = { ...ref };
  for (const [path, value] of Object.entries(patch)) {
    if (value === undefined || value === null || value === '') continue;
    const parts = path.split('.');
    let node = out;
    for (const part of parts.slice(0, -1)) {
      const next = node[part];
      node[part] = next && typeof next === 'object' && !Array.isArray(next) ? { ...(next as Record<string, unknown>) } : {};
      node = node[part] as Record<string, unknown>;
    }
    node[parts[parts.length - 1]] = value;
  }
  return out as T;
}

const isEmpty = (v: unknown) => v === undefined || v === null || v === '';

/**
 * Mark an AI-written description so the import records it as `'ai'`: the
 * accepted text, and (first time only) the text it replaced.
 */
function withAiDescription<E extends ScrapedWatchEntry>(entry: E, fields: Partial<ScrapedWatchEntry>, before: E): E {
  const description = fields.description;
  if (typeof description !== 'string' || !description.trim()) return entry;
  return {
    ...entry,
    _aiDescription: description,
    _preAiDescription: before._preAiDescription ?? before.description ?? '',
  };
}

/**
 * Apply the grid's accepted values to the preview. Parents first: a parent's
 * reviewed values are kept (by parent doc id) for the parent write, and copied
 * onto its variants' EMPTY fields — the free parent → variant fill; the six
 * variant-level fields are each variant's own answer and never copied. Then
 * each row's own ticked values, which win over anything copied.
 */
export function applyPreviewEnrichRows<T extends { entry: ScrapedWatchEntry }>(
  items: readonly T[],
  rows: ReadonlyArray<{ refId: string; values: Record<string, unknown> }>,
  groupIndices: ReadonlyMap<string, readonly number[]>,
  parentValues: ReadonlyMap<string, Partial<ScrapedWatchEntry>>,
): { items: T[]; parentValues: Map<string, Partial<ScrapedWatchEntry>> } {
  const next = [...items];
  const values = new Map(parentValues);
  for (const { refId, values: accepted } of rows) {
    if (refId.startsWith(PREVIEW_ROW_PREFIX)) continue;
    const fields = suggestionValuesToEntryFields(accepted);
    const prevParent = values.get(refId) ?? {};
    // The parent is built from its group's lead row, so that row's text is the
    // one an AI description replaces on the parent.
    const lead = next[(groupIndices.get(refId) ?? [])[0]]?.entry;
    values.set(
      refId,
      withAiDescription(withFields(prevParent as ScrapedWatchEntry, fields), fields, {
        ...(lead ?? {}),
        ...prevParent,
      } as ScrapedWatchEntry),
    );
    for (const i of groupIndices.get(refId) ?? []) {
      const it = next[i];
      if (!it) continue;
      const entry = { ...it.entry } as Record<string, unknown>;
      const copied: Partial<ScrapedWatchEntry> = {};
      for (const [key, value] of Object.entries(fields)) {
        if (key === '_aiFields') continue;
        if (!VARIANT_LEVEL_ENTRY_KEYS.has(key) && isEmpty(entry[key])) {
          entry[key] = value;
          (copied as Record<string, unknown>)[key] = value;
        }
      }
      // Registry-path fields copy the same way, path by path: only where the
      // variant has no answer of its own. None of them is variant-level.
      if (fields._aiFields) {
        const own = (entry._aiFields as Record<string, unknown> | undefined) ?? {};
        const inherited = Object.fromEntries(Object.entries(fields._aiFields).filter(([path]) => isEmpty(own[path])));
        if (Object.keys(inherited).length > 0) entry._aiFields = { ...inherited, ...own };
      }
      // A variant that inherits the parent's AI description is AI-written too.
      next[i] = { ...it, entry: withAiDescription(entry as unknown as ScrapedWatchEntry, copied, it.entry) };
    }
  }
  for (const { refId, values: accepted } of rows) {
    if (!refId.startsWith(PREVIEW_ROW_PREFIX)) continue;
    const i = Number(refId.slice(PREVIEW_ROW_PREFIX.length));
    const it = next[i];
    if (!it) continue;
    const fields = suggestionValuesToEntryFields(accepted);
    next[i] = { ...it, entry: withAiDescription(withFields(it.entry, fields), fields, it.entry) };
  }
  return { items: next, parentValues: values };
}

/**
 * A variant group's parent, built in memory from its lead variant: the clean
 * product name, the product's (not the variant's) images, and no
 * variant-distinguishing fields. Shared by the import's parent pre-pass and
 * the preview-step AI enrichment, so the parent the curator reviews is the one
 * that gets written.
 */
export function buildParentEntry(template: ScrapedWatchEntry, parentRef: string): ScrapedWatchEntry {
  return {
    ...template,
    reference: parentRef,
    title: template.productName || template.title,
    // An expanded store product: the parent shows the PRODUCT's images, not
    // the first variant's.
    imgSrc: template.storeParent?.imgSrc ?? template.imgSrc,
    taggedImages: template.storeParent?.taggedImages ?? template.taggedImages,
    variant: undefined,
    variantCode: undefined,
    variantDesc: undefined,
    strapMaterial: undefined,
    strapColor: undefined,
  };
}
